import { useState } from 'react';
import { Users, Copy, Check, ArrowRight, Wifi, WifiOff } from 'lucide-react';
import { createGameSession, getSessionByJoinCode, recordEvent } from '../../../lib/collaborativeGameService';
import type { BuyAFeatureConfig, CollaborativeGameSession } from '../../../types/collaborativeGame';

interface LiveSessionSetupProps {
  featureInputs: Array<{ name: string; price: number }>;
  budgetPerParticipant: number;
  totalBudget: number;
  onSessionJoined: (sessionId: string, myName: string, isHost: boolean) => void;
  onBack: () => void;
}

type SetupStep = 'choose' | 'create' | 'join';

export function LiveSessionSetup({
  featureInputs,
  budgetPerParticipant,
  totalBudget,
  onSessionJoined,
  onBack,
}: LiveSessionSetupProps) {
  const [step, setStep] = useState<SetupStep>('choose');
  const [hostName, setHostName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [participantName, setParticipantName] = useState('');
  const [createdSession, setCreatedSession] = useState<CollaborativeGameSession | null>(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [joinError, setJoinError] = useState('');

  const handleCreate = async () => {
    if (!hostName.trim() || featureInputs.length === 0) return;
    setLoading(true);
    try {
      const config: BuyAFeatureConfig = {
        features: featureInputs.map((f, i) => ({ id: `f-${i}`, name: f.name, price: f.price })),
        budget_per_participant: budgetPerParticipant,
        total_budget: totalBudget,
      };
      const session = await createGameSession('buy-a-feature', hostName.trim(), config);
      // Record host join
      await recordEvent(session.id, hostName.trim(), 'join', { isHost: true });
      setCreatedSession(session);
      setStep('create');
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (!createdSession) return;
    navigator.clipboard.writeText(createdSession.joinCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyLink = () => {
    if (!createdSession) return;
    const url = `${window.location.origin}${window.location.pathname}?session=${createdSession.joinCode}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleHostStart = () => {
    if (!createdSession || !hostName.trim()) return;
    onSessionJoined(createdSession.id, hostName.trim(), true);
  };

  const handleJoin = async () => {
    if (!joinCode.trim() || !participantName.trim()) return;
    setLoading(true);
    setJoinError('');
    try {
      const session = await getSessionByJoinCode(joinCode.trim());
      if (!session) {
        setJoinError('Session not found. Check the code and try again.');
        return;
      }
      if (session.status === 'ended') {
        setJoinError('This session has already ended.');
        return;
      }
      await recordEvent(session.id, participantName.trim(), 'join', { isHost: false });
      onSessionJoined(session.id, participantName.trim(), false);
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : 'Failed to join session');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'choose') {
    return (
      <div className="max-w-2xl mx-auto">
        <button onClick={onBack} className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-6 flex items-center gap-1">
          ← Back
        </button>
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">Live Session</h2>
        <p className="text-[var(--text-sec)] mb-8">
          Everyone joins on their own device and buys simultaneously. The conversation — who bought what and why — is preserved as the session transcript.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => setStep('create')}
            disabled={featureInputs.length === 0}
            className="p-6 border-2 border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-all text-left disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <div className="w-10 h-10 bg-[var(--slate-dim)] flex items-center justify-center mb-3">
              <Users className="h-5 w-5 text-[var(--text-ter)]" />
            </div>
            <h3 className="font-semibold text-[var(--text-pri)] mb-1">Host a session</h3>
            <p className="text-sm text-[var(--text-sec)]">
              Create the feature list, get a join code, invite participants.
              {featureInputs.length === 0 && <span className="block text-[var(--mustard)] mt-1">Add features first.</span>}
            </p>
          </button>
          <button
            onClick={() => setStep('join')}
            className="p-6 border-2 border-[var(--rule)] hover:border-[var(--green)] hover:bg-[var(--green-dim)] transition-all text-left"
          >
            <div className="w-10 h-10 bg-[var(--green-dim)] flex items-center justify-center mb-3">
              <ArrowRight className="h-5 w-5 text-[var(--green)]" />
            </div>
            <h3 className="font-semibold text-[var(--text-pri)] mb-1">Join a session</h3>
            <p className="text-sm text-[var(--text-sec)]">Enter the code the host shared with you.</p>
          </button>
        </div>
      </div>
    );
  }

  if (step === 'create' && !createdSession) {
    return (
      <div className="max-w-lg mx-auto">
        <button onClick={() => setStep('choose')} className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-6 flex items-center gap-1">
          ← Back
        </button>
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-6">Host a session</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Your name</label>
            <input
              type="text"
              value={hostName}
              onChange={e => setHostName(e.target.value)}
              placeholder="e.g. Alex"
              className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
            />
          </div>
          <div className="p-4 bg-[var(--surface)] text-sm text-[var(--text-sec)] space-y-1">
            <div><strong>{featureInputs.length}</strong> features on the menu</div>
            <div>Budget per participant: <strong>${budgetPerParticipant.toFixed(0)}</strong></div>
          </div>
          {joinError && <p className="text-[var(--coral)] text-sm">{joinError}</p>}
          <button
            onClick={handleCreate}
            disabled={!hostName.trim() || loading}
            className="w-full px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
          >
            {loading ? 'Creating…' : 'Create session'}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'create' && createdSession) {
    return (
      <div className="max-w-lg mx-auto">
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">Session ready</h2>
        <p className="text-[var(--text-sec)] mb-6">Share this code or link with participants. Start when everyone has joined.</p>

        {/* Join code */}
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6 mb-4 text-center">
          <p className="text-sm text-[var(--text-sec)] font-medium mb-2">Join code</p>
          <div className="text-5xl font-bold tracking-widest text-[var(--text-sec)] font-mono mb-4">
            {createdSession.joinCode}
          </div>
          <div className="flex gap-2 justify-center">
            <button
              onClick={handleCopyCode}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-raised)] border border-[var(--slate)] text-sm text-[var(--text-ter)] hover:bg-[var(--slate-dim)] transition-colors"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied!' : 'Copy code'}
            </button>
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-raised)] border border-[var(--slate)] text-sm text-[var(--text-ter)] hover:bg-[var(--slate-dim)] transition-colors"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              Copy link
            </button>
          </div>
        </div>

        <button
          onClick={handleHostStart}
          className="w-full px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] font-medium transition-colors flex items-center justify-center gap-2"
        >
          <Wifi className="h-5 w-5" />
          Open session lobby
        </button>
      </div>
    );
  }

  // Join step
  return (
    <div className="max-w-lg mx-auto">
      <button onClick={() => setStep('choose')} className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-6 flex items-center gap-1">
        ← Back
      </button>
      <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-6">Join a session</h2>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Your name</label>
          <input
            type="text"
            value={participantName}
            onChange={e => setParticipantName(e.target.value)}
            placeholder="e.g. Jordan"
            className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Session code</label>
          <input
            type="text"
            value={joinCode}
            onChange={e => setJoinCode(e.target.value.toUpperCase())}
            placeholder="e.g. AB3KX7"
            maxLength={6}
            className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-xl tracking-widest uppercase"
            onKeyDown={e => e.key === 'Enter' && handleJoin()}
          />
        </div>
        {joinError && <p className="text-[var(--coral)] text-sm">{joinError}</p>}
        <button
          onClick={handleJoin}
          disabled={!joinCode.trim() || !participantName.trim() || loading}
          className="w-full px-6 py-3 bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center justify-center gap-2"
        >
          {loading ? 'Joining…' : (
            <><WifiOff className="h-5 w-5" />Join session</>
          )}
        </button>
      </div>
    </div>
  );
}
